/**
 * DebitNoteFormDialog — New / Edit for Sales Control › Debit Note.
 * Converted 1:1 from Access `frmCtrlSalesQryDebitAdd` (NO Copy mode). 2-col form
 * (Access Left 2592 → col 1, Left 9600 → col 2) ordered by Top.
 *
 * Left col:  ID(ro) · Debit No.(index) · Debit Code(gen,ro) · Debit Date ·
 *            Debit Amount · Price Unit(combo) · CP List(combo) ·
 *            "* Only choose CP List or Service" · Debitor to(ro) ·
 *            Debitor Name(ro) · Debitor's Ref No · Reason Debit · Remark(note)
 * Right col: Claim ID(combo) · Claim Type(ro) · Claim Value(ro) ·
 *            Claim Currency(ro) · Invoice No(ro) · FX · Service(combo)
 *
 * CP List ↔ Service are MUTUALLY EXCLUSIVE (Access cp_code_AfterUpdate /
 * service_code_AfterUpdate clear each other). Picking one sets
 * debitor_to = option meta.debitorTo, debitor_name = meta.name and clears the
 * other. On save debit_cp_code = whichever code is chosen, debitor_to = its meta.
 *
 * Cascade (claim_debit_no_index_AfterUpdate + claim_scs_code_AfterUpdate):
 *   both rebuild debit_no = "DN" + zeroPad(index,4) + "/" + invoiceNo
 *   (invoiceNo from claim snapshot). After rebuild, check duplicate debit_no →
 *   if exists warn "Debit Code is existed!" and clear.
 *
 * Required (strCheck): claim_debit_no_index, claim_scs_code, debit_no, debit_date.
 * Edit disables claim_debit_no_index + claim_scs_code; no dup check.
 */
import { useEffect, useRef, useState } from 'react'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'
import {
  Alert, Autocomplete, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'

import {
  type DnLookupItem,
  type SaleDebitNoteWrite,
  useCreateSaleDebitNoteMutation,
  useUpdateSaleDebitNoteMutation,
  useLazyGetSaleDebitNoteFormQuery,
  useLazyGetDebitClaimSnapshotQuery,
  useLazySuggestDebitNoQuery,
  useLazyCheckDebitNoQuery,
  useDnLookupClaimCodesQuery,
  useDnLookupPriceUnitsQuery,
  useDnLookupCpListQuery,
  useDnLookupServicesQuery,
} from '../../store/api/saleControlApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  debitNoteId: number | null
  canEdit: boolean
  onClose: () => void
  onSaved: () => void
}

interface Draft {
  claimScsCode: string
  claimDebitNoIndex: string
  debitNo: string
  debitDate: string
  debitValue: string
  priceUnit: string
  cpCode: string
  serviceCode: string
  debitorRefNo: string
  debitReason: string
  note: string
  fx: string
  // readonly display (row id + combo meta + claim snapshot)
  claimDebitScsId: string
  debitorTo: string
  debitorName: string
  claimType: string
  claimValue: string
  claimCurrency: string
  invoiceNo: string
}

const EMPTY: Draft = {
  claimScsCode: '', claimDebitNoIndex: '', debitNo: '', debitDate: '', debitValue: '',
  priceUnit: '', cpCode: '', serviceCode: '', debitorRefNo: '', debitReason: '', note: '', fx: '',
  claimDebitScsId: '', debitorTo: '', debitorName: '',
  claimType: '', claimValue: '', claimCurrency: '', invoiceNo: '',
}

const SM = 'small' as const
const compactInputSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
}
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const gridSx: SxProps<Theme> = {
  display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
  columnGap: 1.5, rowGap: 1.25, alignItems: 'start',
}
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}
const numProps = { style: { textAlign: 'right' as const }, inputMode: 'decimal' as const }

function parseNum(v: string): number | null {
  const str = (v ?? '').toString().replace(/,/g, '').trim()
  if (!str) return null
  const n = parseFloat(str)
  return Number.isFinite(n) ? n : null
}
function fmtStd(v: unknown): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : parseNum(String(v))
  if (n == null) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
const s = (v: unknown): string => (v == null ? '' : String(v))

function buildDebitNo(index: string, invoiceNo: string): string {
  const idx = index.trim()
  if (!idx) return ''
  const n = parseInt(idx, 10)
  if (!Number.isFinite(n)) return ''
  return `DN${String(n).padStart(4, '0')}/${invoiceNo}`
}

const fullLabel = (o: DnLookupItem): string =>
  (o.value === o.label || !o.label ? String(o.value ?? '') : `${o.value} — ${o.label}`)

function Combo(props: {
  label: string
  value: string
  options: DnLookupItem[]
  loading?: boolean
  required?: boolean
  disabled?: boolean
  error?: boolean
  tabIndex?: number
  /** Field có ô name riêng (vd CP List có Debitor Name) → field chỉ show code
   *  sau khi chọn; dropdown vẫn "<code> — <name>". */
  codeOnly?: boolean
  onPick: (value: string, meta: DnLookupItem['meta']) => void
}) {
  const { label, value, options, loading, required, disabled, error, tabIndex, codeOnly, onPick } = props
  const sel = value ? options.find((o) => o.value === value) ?? { value, label: value } : null
  const opts = sel && !options.some((o) => o.value === sel.value) ? [sel, ...options] : options
  return (
    <Autocomplete
      size={SM}
      fullWidth
      disabled={disabled}
      options={opts}
      value={sel}
      loading={loading}
      onChange={(_e, v) => onPick(v?.value ?? '', v?.meta ?? null)}
      getOptionLabel={(o) => (!o ? '' : codeOnly ? String(o.value) : fullLabel(o))}
      renderOption={(liProps, o) => <li {...liProps} key={o.value}>{fullLabel(o)}</li>}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          error={error}
          inputProps={{ ...params.inputProps, tabIndex }}
        />
      )}
    />
  )
}

export default function DebitNoteFormDialog({ open, mode, debitNoteId, canEdit, onClose, onSaved }: Props) {
  const snack = useSnackbar()
  const [draft, setDraft] = useState<Draft>(EMPTY)
  // Edit hydrate hits a view query → show a loading state until the API response
  // lands, then render the fields (avoid a flash of empty inputs).
  const [formLoading, setFormLoading] = useState(false)
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((p) => ({ ...p, [k]: v }))

  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  const [loadForm] = useLazyGetSaleDebitNoteFormQuery()
  const [loadSnapshot] = useLazyGetDebitClaimSnapshotQuery()
  const [suggest] = useLazySuggestDebitNoQuery()
  const [checkDebit] = useLazyCheckDebitNoQuery()
  const [createDebit, { isLoading: creating }] = useCreateSaleDebitNoteMutation()
  const [updateDebit, { isLoading: updating }] = useUpdateSaleDebitNoteMutation()
  const saving = creating || updating

  const skip = { skip: !open }
  const claimCodes = useDnLookupClaimCodesQuery(undefined, skip)
  const priceUnits = useDnLookupPriceUnitsQuery(undefined, skip)
  const cpList = useDnLookupCpListQuery(undefined, skip)
  const services = useDnLookupServicesQuery(undefined, skip)

  const readonly = mode === 'edit'

  // ── rebuild debit_no + duplicate check (both AfterUpdate cascades) ──────────
  const rebuildAndCheck = async (index: string, invoiceNo: string) => {
    const dn = buildDebitNo(index, invoiceNo)
    setDraft((p) => ({ ...p, debitNo: dn }))
    if (!dn || readonly) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await checkDebit({ debitNo: dn }).unwrap()
      if (check.exists) {
        setDraft((p) => ({ ...p, debitNo: '' }))
        snack.warning('Debit Code is existed!')
      }
    } catch {
      // BE re-checks on save; do not poison UX.
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  // ── claim snapshot → readonly display + invoiceNo (returns invoiceNo) ───────
  const applySnapshot = async (claimScsCode: string): Promise<string> => {
    if (!claimScsCode) {
      setDraft((p) => ({ ...p, claimType: '', claimValue: '', claimCurrency: '', invoiceNo: '' }))
      return ''
    }
    const snap = await loadSnapshot(claimScsCode).unwrap().catch(() => null)
    const invoiceNo = snap?.invoiceNo ?? ''
    setDraft((p) => ({
      ...p,
      claimType: snap?.claimType ?? '',
      claimValue: fmtStd(snap?.claimValue),
      claimCurrency: snap?.claimCurrency ?? '',
      invoiceNo,
    }))
    return invoiceNo
  }

  // ── Hydrate on open (deps: [open, mode, debitNoteId] only) ──────────────────
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    if (mode !== 'new') setFormLoading(true) // show loading immediately for Edit
    void (async () => {
      if (mode === 'new') {
        setFormLoading(false)
        const res = await suggest().unwrap().catch(() => null)
        if (cancelled) return
        // New: prefill Debit Date = Now (hôm nay, local — tránh lệch timezone của
        // toISOString UTC). Chị Đẹp 2026-07-14.
        const now = new Date()
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
        setDraft({ ...EMPTY, claimDebitNoIndex: res ? String(res.index) : '', debitDate: today })
        return
      }
      if (!debitNoteId) { setFormLoading(false); return }
      const f = await loadForm(debitNoteId).unwrap().catch(() => null)
      if (cancelled) return
      if (!f) { setFormLoading(false); return }
      const g = (k: string) => s((f as Record<string, unknown>)[k])
      const debitNo = g('debitNo')
      // Edit: derive index from debitNo = "DN0001/<invoice>" → 1
      const index = debitNo.split('/')[0].replace(/^DN/i, '').replace(/^0+/, '')
      const claimScsCode = g('claimScsCode')
      const debitorTo = g('debitorTo')
      const debitCpCode = g('debitCpCode')
      // debitorTo === 'client' → CP List chosen; otherwise Service chosen.
      const isCp = debitorTo === 'client'
      setDraft({
        ...EMPTY,
        claimScsCode,
        claimDebitNoIndex: index,
        debitNo,
        debitDate: g('debitDate').slice(0, 10),
        debitValue: fmtStd(g('debitValue')),
        priceUnit: g('priceUnit'),
        cpCode: isCp ? debitCpCode : '',
        serviceCode: isCp ? '' : debitCpCode,
        debitorRefNo: g('debitorRefNo'),
        debitReason: g('debitReason'),
        note: g('note'),
        fx: fmtStd(g('fx')),
        claimDebitScsId: String(debitNoteId),
        debitorTo,
      })
      setFormLoading(false)
      // Fill readonly display from the claim snapshot.
      void applySnapshot(claimScsCode)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, debitNoteId])

  // ── Edit: resolve debitor_name display once cp/service options load ─────────
  useEffect(() => {
    if (!open || mode !== 'edit' || draft.debitorName) return
    if (draft.cpCode) {
      const o = (cpList.data?.items ?? []).find((i) => i.value === draft.cpCode)
      if (o?.meta?.name) setDraft((p) => ({ ...p, debitorName: o.meta?.name ?? '' }))
    } else if (draft.serviceCode) {
      const o = (services.data?.items ?? []).find((i) => i.value === draft.serviceCode)
      if (o?.meta?.name) setDraft((p) => ({ ...p, debitorName: o.meta?.name ?? '' }))
    }
  }, [open, mode, draft.cpCode, draft.serviceCode, draft.debitorName, cpList.data, services.data])

  // ── Cascades ────────────────────────────────────────────────────────────────
  const onIndexChange = (index: string) => {
    setDraft((p) => ({ ...p, claimDebitNoIndex: index }))
    void rebuildAndCheck(index, draft.invoiceNo)
  }
  const onClaimPick = (claimScsCode: string) => {
    setDraft((p) => ({ ...p, claimScsCode }))
    void (async () => {
      const invoiceNo = await applySnapshot(claimScsCode)
      await rebuildAndCheck(draft.claimDebitNoIndex, invoiceNo)
    })()
  }
  // CP List picked → set debitor from meta, clear Service (mutually exclusive).
  const onCpPick = (value: string, meta: DnLookupItem['meta']) => {
    setDraft((p) => (value
      ? { ...p, cpCode: value, serviceCode: '', debitorTo: meta?.debitorTo ?? '', debitorName: meta?.name ?? '' }
      : { ...p, cpCode: '', debitorTo: '', debitorName: '' }))
  }
  // Service picked → set debitor from meta, clear CP List (mutually exclusive).
  const onServicePick = (value: string, meta: DnLookupItem['meta']) => {
    setDraft((p) => (value
      ? { ...p, serviceCode: value, cpCode: '', debitorTo: meta?.debitorTo ?? '', debitorName: meta?.name ?? '' }
      : { ...p, serviceCode: '', debitorTo: '', debitorName: '' }))
  }

  const indexEmpty = !draft.claimDebitNoIndex.trim()
  const claimEmpty = !draft.claimScsCode.trim()
  const debitNoEmpty = !draft.debitNo.trim()
  const dateEmpty = !draft.debitDate.trim()
  const canSave = canEdit && !indexEmpty && !claimEmpty && !debitNoEmpty && !dateEmpty && !saving && !precheckBusy

  const handleClose = () => { if (!saving) onClose() }

  const handleSave = async () => {
    if (precheckBusyRef.current) return
    if (!canSave) return
    const chosenCode = draft.cpCode || draft.serviceCode
    const payload: SaleDebitNoteWrite = {
      claimScsCode: draft.claimScsCode || null,
      debitNo: draft.debitNo || null,
      debitDate: draft.debitDate || null,
      debitorTo: chosenCode ? (draft.debitorTo || null) : null,
      debitCpCode: chosenCode || null,
      debitorRefNo: draft.debitorRefNo || null,
      debitReason: draft.debitReason || null,
      debitValue: parseNum(draft.debitValue),
      note: draft.note || null,
      priceUnit: draft.priceUnit || null,
      fx: parseNum(draft.fx),
    }
    try {
      if (mode === 'edit' && debitNoteId) {
        await updateDebit({ id: debitNoteId, body: payload }).unwrap()
        snack.success('Successfully Updated')
      } else {
        await createDebit(payload).unwrap()
        snack.success('Successfully Added')
      }
      onSaved()
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Save debit note failed'))
    }
  }

  const title = mode === 'new' ? 'New' : 'Edit'

  const dateField = (key: keyof Draft, label: string, required?: boolean, tabIndex?: number) => (
    <DatePicker
      label={label}
      format="DD-MMM-YYYY"
      value={draft[key] ? dayjs(draft[key] as string) : null}
      onChange={(d) => set(key, (d && d.isValid() ? d.format('YYYY-MM-DD') : '') as Draft[typeof key])}
      slotProps={{
        textField: {
          size: SM, fullWidth: true, required,
          error: required && !(draft[key] as string),
          InputLabelProps: { shrink: true },
          inputProps: { tabIndex },
        },
      }}
    />
  )
  const numField = (key: keyof Draft, label: string, tabIndex?: number) => (
    <TextField
      label={label} size={SM} fullWidth type="text" value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      onBlur={() => set(key, fmtStd(draft[key] as string) as Draft[typeof key])}
      inputProps={{ ...numProps, tabIndex }}
    />
  )
  const textField = (key: keyof Draft, label: string, tabIndex?: number, extra?: object) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      onChange={(e) => set(key, e.target.value as Draft[typeof key])}
      inputProps={{ tabIndex }} {...extra} />
  )
  const roField = (key: keyof Draft, label: string, numeric?: boolean) => (
    <TextField label={label} size={SM} fullWidth value={draft[key] as string}
      InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
      inputProps={{ tabIndex: -1, ...(numeric ? { style: { textAlign: 'right' } } : {}) }} />
  )
  const cell = (col: number, row: number | string, node: React.ReactNode) => (
    <Box sx={{ gridColumn: { sm: col }, gridRow: { sm: row } }}>{node}</Box>
  )

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; handleClose() }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" noWrap title={`Outbound Logistics - Debit Note — ${title}`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700 }}>
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Outbound Logistics - </Box>Debit Note — {title}
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="success" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>Cancel</Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={compactInputSx}>
        {formLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
        <>
        {!canEdit && <Alert severity="info" sx={{ mb: 1.5 }}>You do not have permission to edit debit notes.</Alert>}
        <Box sx={gridSx}>
          {/* ── Left column (Access Left=2592) ── */}
          {cell(1, 1, roField('claimDebitScsId', 'ID'))}
          {cell(1, 2, (
            <TextField
              label="Debit No. *" size={SM} fullWidth disabled={readonly}
              value={draft.claimDebitNoIndex}
              onChange={(e) => onIndexChange(e.target.value)}
              error={indexEmpty} inputProps={{ style: { textAlign: 'right' }, inputMode: 'numeric', tabIndex: 1 }}
              helperText={readonly ? 'Debit No. cannot be changed after creation.' : undefined}
            />
          ))}
          {cell(1, 3, (
            <TextField
              label="Debit Code *" size={SM} fullWidth value={draft.debitNo}
              InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={readonlyFieldSx}
              error={debitNoEmpty}
              inputProps={{ tabIndex: -1 }}
              helperText={precheckBusy ? 'Verifying Debit Code…' : 'Generated from Debit No. and Claim ID.'}
            />
          ))}
          {cell(1, 4, dateField('debitDate', 'Debit Date', true, 3))}
          {cell(1, 5, numField('debitValue', 'Debit Amount', 4))}
          {cell(1, 6, (
            <Combo label="Price Unit" value={draft.priceUnit} options={priceUnits.data?.items ?? []}
              loading={priceUnits.isFetching} tabIndex={5} onPick={(v) => set('priceUnit', v)} />
          ))}
          {cell(1, 7, (
            <Combo label="CP List" value={draft.cpCode} options={cpList.data?.items ?? []}
              loading={cpList.isFetching} tabIndex={6} codeOnly onPick={onCpPick} />
          ))}
          {/* hint under the CP List / Service pair — spans both columns */}
          <Box sx={{ gridColumn: { sm: '1 / span 2' }, gridRow: { sm: 8 } }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: -0.5 }}>
              * Only choose CP List or Service
            </Typography>
          </Box>
          {cell(1, 9, roField('debitorTo', 'Debitor to'))}

          {/* ── Right column (Access Left=9600) ── */}
          {cell(2, 1, (
            <Combo label="Claim ID" value={draft.claimScsCode} required disabled={readonly}
              error={claimEmpty} options={claimCodes.data?.items ?? []} loading={claimCodes.isFetching}
              tabIndex={2} onPick={onClaimPick} />
          ))}
          {cell(2, 2, roField('claimType', 'Claim Type'))}
          {cell(2, 3, roField('claimValue', 'Claim Value', true))}
          {cell(2, 4, roField('claimCurrency', 'Claim Currency'))}
          {cell(2, 5, roField('invoiceNo', 'Invoice No'))}
          {cell(2, 6, numField('fx', 'FX', 8))}
          {cell(2, 7, (
            <Combo label="Service" value={draft.serviceCode} options={services.data?.items ?? []}
              loading={services.isFetching} tabIndex={7} codeOnly onPick={onServicePick} />
          ))}

          {/* ── Bottom section (debitor + note), balanced across both columns ── */}
          {cell(2, 9, roField('debitorName', 'Debitor Name'))}
          {cell(1, 10, textField('debitorRefNo', "Debitor's Ref No", 9))}
          {cell(2, 10, textField('debitReason', 'Reason Debit', 10))}
          <Box sx={{ gridColumn: { sm: '1 / span 2' }, gridRow: { sm: 11 } }}>
            {textField('note', 'Remark', 11, { multiline: true, minRows: 3 })}
          </Box>
        </Box>
        </>
        )}
      </DialogContent>
    </Dialog>
  )
}
